import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import SystemMessageCard from './SystemMessageCard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SystemMessage {
  id: string;
  title: string;
  body: string;
  created_at: string;
  target_type: 'all' | 'all_clients' | 'all_practitioners' | 'specific_user';
}

interface SystemMessageExtended extends SystemMessage {
  target_user_id?: string | null;
  expires_at?: string | null;
}

interface SystemMessagesListProps {
  userType: 'client' | 'practitioner';
  className?: string;
}

const SystemMessagesList: React.FC<SystemMessagesListProps> = ({
  userType,
  className = ""
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        const nowIso = new Date().toISOString();

        // Rely on RLS to return only messages the current user can see
        const { data, error } = await supabase
          .from('system_messages')
          .select('id, title, body, created_at, target_type, expires_at, is_active')
          .eq('is_active', true)
          .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          console.error('Error fetching system messages:', error);
          return;
        }

        setMessages((data || []).slice(0, 10) as SystemMessage[]);
      } catch (err) {
        console.error('Unexpected error fetching messages:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();

    // Set up real-time subscription for new/updated/deleted messages
    const channel = supabase
      .channel('system-messages-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_messages'
        },
        (payload) => {
          const newMessage = payload.new as SystemMessageExtended & { is_active?: boolean; target_user_email?: string };
          const nowIso = new Date().toISOString();

          const isRelevant = (
            (newMessage.target_type === 'specific_user' && (newMessage.target_user_id === user?.id || (newMessage as any).target_user_email === (user as any)?.email)) ||
            newMessage.target_type === 'all' ||
            (newMessage.target_type === 'all_clients' && userType === 'client') ||
            (newMessage.target_type === 'all_practitioners' && userType === 'practitioner')
          );

          const notExpired = !newMessage.expires_at || newMessage.expires_at > nowIso;
          const active = (newMessage as any).is_active !== false;

          if (!isRelevant || !notExpired || !active) return;

          setMessages(current => [newMessage as SystemMessage, ...current].slice(0, 10));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_messages'
        },
        (payload) => {
          const updated = payload.new as SystemMessageExtended & { is_active?: boolean; target_user_email?: string };
          const nowIso = new Date().toISOString();
          const isRelevant = (
            (updated.target_type === 'specific_user' && (updated.target_user_id === user?.id || (updated as any).target_user_email === (user as any)?.email)) ||
            updated.target_type === 'all' ||
            (updated.target_type === 'all_clients' && userType === 'client') ||
            (updated.target_type === 'all_practitioners' && userType === 'practitioner')
          );
          const notExpired = !updated.expires_at || updated.expires_at > nowIso;
          const active = (updated as any).is_active !== false;

          setMessages(current => {
            // Remove if no longer valid
            if (!isRelevant || !notExpired || !active) {
              return current.filter(m => m.id !== (updated as any).id);
            }
            // Update or insert at top
            const others = current.filter(m => m.id !== (updated as any).id);
            return [{ ...(updated as any) }, ...others].slice(0, 10) as SystemMessage[];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'system_messages'
        },
        (payload) => {
          const deletedId = (payload.old as any)?.id;
          if (!deletedId) return;
          setMessages(current => current.filter(m => m.id !== deletedId));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, userType]);

  if (isLoading) {
    return (
      <div className={`bg-muted/30 rounded-md sm:rounded-lg p-4 sm:p-6 w-full ${className}`}>
        <div className="flex items-center space-x-2 mb-4">
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">{t('dashboard:systemMessages')}</h3>
        </div>
        <div className="space-y-3">
          <div className="h-24 bg-muted rounded animate-pulse" />
          <div className="h-24 bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`bg-muted/30 rounded-md sm:rounded-lg p-4 sm:p-6 w-full ${className}`}>
        <div className="flex items-center space-x-2 mb-4">
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">{t('dashboard:systemMessages')}</h3>
        </div>
        <p className="text-muted-foreground text-center py-8">{t('dashboard:noSystemMessages')}</p>
      </div>
    );
  }

  return (
    <div className={`bg-muted/30 rounded-md sm:rounded-lg p-4 sm:p-6 w-full ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <MessageSquare className="w-5 h-5 text-muted-foreground" />
        <h3 className="font-semibold text-foreground">{t('dashboard:systemMessages')}</h3>
      </div>
      
      <div className="relative">
        <ScrollArea className="h-80">
          <div className="space-y-3 pr-2 sm:pr-4">
            {messages.slice(0, 2).map((message) => (
              <SystemMessageCard
                key={message.id}
                id={message.id}
                title={message.title}
                body={message.body}
                createdAt={message.created_at}
              />
            ))}
            
            {messages.length > 2 && (
              <div className="space-y-3">
                {messages.slice(2).map((message) => (
                  <SystemMessageCard
                    key={message.id}
                    id={message.id}
                    title={message.title}
                    body={message.body}
                    createdAt={message.created_at}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
        
        {messages.length > 2 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/30 to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
};

export default SystemMessagesList;